import React, { useEffect, useRef, useState } from 'react';
import { Modal, Button, Divider, Input, Popover, Spin } from 'antd';
import styles from './index.module.scss';
import { Person, Relation, StaffLabel } from 'type/contact';
import Avatar from 'component/Avatar';
import { CONTACT_TYPE, INFO_TITLE } from 'constant/contact';
import Icon from 'component/Icon';
import { useDispatch, useSelector } from 'react-redux';
import { addContact, deleteContact, editContact, getStaffLabelList } from 'model/contact';
import classnames from 'classnames';
import ConfirmModal from 'component/ConfirmModal';
import { getStaffId } from 'data/storage';
import { RootState } from 'model';
import { fridayAgent } from '@cvte/react-fridayagent3';
import FRIDAYAGENT_EVENTS from 'constant/fridayAgentEvent';
import tracker from 'lib/tracker';
import { Meeting } from 'api';

interface IProps {
  show: boolean;
  handelClose: () => void;
  contactInfo: Person;
  type: string;
  labelId: number;
}

const ContactCard: React.FunctionComponent<IProps> = ({ show, handelClose, contactInfo, type, labelId }) => {
  const [editRemark, setEditRemark] = useState(false);
  const {
    memberName,
    displayName,
    realName,
    realname,
    name,
    avatar,
    mobile,
    orgId,
    email,
    company,
    department,
    jobTitle,
    staffId,
    _id,
    refId,
    cardPhoto,
    teamName
  } = contactInfo;

  const [newRemark, setNewRemark] = useState('');
  const [isOmit, setIsOmit] = useState(false);
  const [relation, setRelation] = useState(contactInfo.relation);
  const staffLabelList: StaffLabel[] = useSelector((state: RootState) => state.contact.staffLabelList);
  const labelLoading: boolean = useSelector((state: RootState) => state.contact.labelLoading);
  const currentId = staffId || _id || refId || '';
  const labelWidth = useRef<HTMLDivElement>(null);

  const contactName = memberName || displayName || realName || realname || name;

  const listData = [
    {
      text: INFO_TITLE.NAME,
      value: contactName,
      need: true
    },
    {
      text: INFO_TITLE.REMARK,
      need: !relation || relation !== Relation.NONE || type === CONTACT_TYPE.SEARCH
    },
    {
      text: INFO_TITLE.LABEL,
      value: staffLabelList.map((s) => s.name).join('，'),
      need: !!staffLabelList.map((s) => s.name).length
    },
    {
      text: INFO_TITLE.MOBILE,
      value: orgId && relation && relation === Relation.EACH_ADDED ? mobile : '--',
      need: true
    },
    {
      text: INFO_TITLE.EMAIL,
      value: email,
      need: true
    },
    {
      text: INFO_TITLE.COMPANY,
      value: company,
      need: !orgId
    },
    {
      text: INFO_TITLE.DEPARTMENT,
      value: department || teamName,
      need: orgId
    },
    {
      text: INFO_TITLE.JOB,
      value: jobTitle,
      need: true
    }
  ];

  useEffect(() => {
    // 埋点
    show && fridayAgent.pushData({ ...FRIDAYAGENT_EVENTS.WEB_TEAMS_CLICK_CONTACT_DETAIL });
    const subscription = Meeting.getUserRelation(currentId).subscribe((data: any) => {
      if (data && data.relation) setRelation(data.relation);
    });
    // 清理函数，取消订阅
    return () => {
      subscription.unsubscribe();
    };
  }, [contactInfo]);

  useEffect(() => {
    setNewRemark(contactInfo.remark);
    dispatch(getStaffLabelList(currentId));
  }, [contactInfo]);

  useEffect(() => {
    console.log(labelWidth.current);
  }, [labelWidth.current]);

  const noRelation = () => {
    return !relation || relation === Relation.NONE;
  };

  const dispatch = useDispatch();
  const handelAddOrDelete = () => {
    if (noRelation()) {
      tracker?.reporteContactOperation('添加联系人');
      dispatch(addContact({ id: currentId, staffId: currentId, labelId })) && cardClose();
    } else {
      ConfirmModal({
        title: '确定要删除联系人吗？',
        onOk: () => {
          tracker?.reporteContactOperation('删除联系人');
          dispatch(deleteContact({ staffId: currentId, type, labelId }));
          cardClose();
        }
      });
    }
  };

  const editRemarkClick = () => {
    setEditRemark(true);
  };

  const editRemarkEnter = (e: React.KeyboardEvent<HTMLInputElement>, staffId: string) => {
    _editRemark((e.target as HTMLInputElement).value, staffId);
  };

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    _editRemark((e.target as HTMLInputElement).value, currentId);
  };

  const _editRemark = (value: string, staffId: string) => {
    const remark = value.replace(/\s+/g, '');
    if (!newRemark && !remark) {
      setEditRemark(false);
      return;
    }
    setNewRemark(remark);
    dispatch(editContact({ remark, staffId, type, labelId }));
    setEditRemark(false);
  };

  const cardClose = () => {
    handelClose();
  };

  const cls = classnames(styles.modal, {
    [styles.button]: !noRelation()
  });

  return (
    <>
      {contactInfo && (
        <Modal
          className={cls}
          forceRender
          visible={show}
          onCancel={cardClose}
          width={400}
          centered
          closeIcon={<></>}
          footer={[
            getStaffId() !== currentId && (
              <Button key="submit" type={noRelation() ? 'primary' : 'link'} onClick={handelAddOrDelete}>
                {noRelation() ? '添加联系人' : '删除联系人'}
              </Button>
            )
          ]}
          destroyOnClose
        >
          <Spin spinning={labelLoading}>
            <div className={styles.header}>
              <div className={styles.name}>{newRemark || contactName}</div>
              <Avatar src={avatar ?? cardPhoto} width={64} height={64} />
            </div>
            <Divider />
            <div className={styles.listData}>
              {listData.map((data) => {
                return (
                  <div key={data.text}>
                    {data.need && (
                      <div className={styles.item}>
                        <div className={styles.title}>{data.text}</div>
                        <div className={styles.value}>{data.text !== INFO_TITLE.REMARK && data.text !== INFO_TITLE.LABEL && (data.value || '--')}</div>

                        {data.text === INFO_TITLE.REMARK &&
                          (editRemark ? (
                            <Input defaultValue={newRemark} onPressEnter={(e) => editRemarkEnter(e, currentId)} onBlur={onBlur} autoFocus maxLength={16} />
                          ) : (
                            <div className={styles.remark}>
                              {newRemark && <div className={styles.remarkText}>{newRemark}</div>}
                              <Icon type="iconcontact_edit_icon" size={16} onClick={editRemarkClick} />
                            </div>
                          ))}

                        {data.text === INFO_TITLE.LABEL && (
                          <div className={styles.labels}>
                            <div
                              className={styles.labelName}
                              ref={(refElem) => {
                                if (refElem) {
                                  setIsOmit(refElem.scrollWidth > refElem.clientWidth);
                                }
                              }}
                            >
                              {data.value}
                            </div>
                            {isOmit && (
                              <div className={styles.labelMore}>
                                <Popover trigger="click" placement="right" content={<div className={styles.labelPopover}>{data.value}</div>}>
                                  <Icon type="icontag_more_icon" size={16} />
                                </Popover>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Spin>
        </Modal>
      )}
    </>
  );
};

export default ContactCard;
